import React, { useState } from "react";
import { Box, TextField, MenuItem, Button, Typography } from "@mui/material";
import useMicroWellApi from "../../../api/MicroWellApi";
import ScienceIcon from "@mui/icons-material/Science";
import ConfirmDialog from "../../Common/ConfirmDialog";

// MicroWell math model options mirror the eMwMathModel enum in the backend.
// Model 0 = standard MicroWell sigmoid; other values are assay-type variants.
const MATH_MODELS = [
    { value: "0", label: "Model 0 (Standard)" },
    { value: "1", label: "Model 1" },
    { value: "2", label: "Model 2" }
];

export default function MicroWellHeader({ onCalibrate, setResultDataMap, rows, parameters }) {
    const [mathModel, setMathModel] = useState("0");
    const [description, setDescription] = useState("");

    const { callApi, loading, error } = useMicroWellApi(parameters, rows);

    const [openValidationDialog, setOpenValidationDialog] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");

    // Transform calibration API response into the history entry shape.
    // MicroWell calibration returns up to 5 curve parameters, a shape factor,
    // a fit factor, and per-level cal signals. bABFCalibrated == 1 means the
    // ABF calibration succeeded.
    const transformApiToHistory = (data) => {
        if (!data) return null;
        const safe = (v) => (v === null || v === undefined || isNaN(v) ? 0 : Number(v));
        const format6 = (v) => safe(v).toFixed(6);

        return {
            bABFCalibrated: data.bABFCalibrated,
            sMwCalParameterCount: data.sMwCalParameterCount,
            P1: format6(data.fMwCalParameter1),
            P2: format6(data.fMwCalParameter2),
            P3: format6(data.fMwCalParameter3),
            P4: format6(data.fMwCalParameter4),
            P5: format6(data.fMwCalParameter5),
            ShapeFactor: format6(data.fCalShapeFactor),
            FitFactor: format6(data.fCalFitFactor),
            SignalMax: format6(data.fCalSignalMax),
            ConcMax: format6(data.fCalConcentrationMax),
            ConditionCode: data.zConditionCode || "",
            Error: data.zError || ""
        };
    };

    // Enhanced validation with detailed error reporting
    const validateRows = (rows) => {
        if (!rows || rows.length === 0) {
            return { ok: false, error: "No calibration data entered." };
        }

        // Check for empty rows
        const emptyRowIdx = rows.findIndex(
            r => (r.conc === "" || r.conc === null || r.conc === undefined) &&
                 (r.resp === "" || r.resp === null || r.resp === undefined)
        );
        if (emptyRowIdx >= 0) {
            return { ok: false, error: `Row ${emptyRowIdx + 1}: Both Concentration and Replicates are empty.` };
        }

        // Check for mismatched pairs
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            const hasConc = r.conc !== "" && r.conc !== null && r.conc !== undefined;
            const hasResp = r.resp !== "" && r.resp !== null && r.resp !== undefined;
            if (hasConc !== hasResp) {
                return { ok: false, error: `Row ${i + 1}: Concentration and Replicates (ALU) must both be filled or both empty.` };
            }
        }

        // Check for non-numeric values
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (r.conc !== "" && r.conc !== null && r.conc !== undefined) {
                const cNum = Number(r.conc);
                if (isNaN(cNum)) {
                    return { ok: false, error: `Row ${i + 1}: Concentration '${r.conc}' is not a valid number.` };
                }
            }
            if (r.resp !== "" && r.resp !== null && r.resp !== undefined) {
                // resp can be space/comma-separated replicates
                const repsStr = String(r.resp).trim();
                const reps = repsStr.split(/[\s,]+/).filter(v => v !== "");
                for (const rep of reps) {
                    if (isNaN(Number(rep))) {
                        return { ok: false, error: `Row ${i + 1}: ALU replicate '${rep}' is not a valid number.` };
                    }
                }
            }
        }

        // Check that concentrations are strictly increasing
        const concValues = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (r.conc !== "" && r.conc !== null && r.conc !== undefined) {
                concValues.push({ idx: i, val: Number(r.conc) });
            }
        }
        for (let i = 1; i < concValues.length; i++) {
            if (concValues[i].val <= concValues[i - 1].val) {
                return {
                    ok: false,
                    error: `Row ${concValues[i].idx + 1}: Concentrations must be strictly increasing. ` +
                           `${concValues[i - 1].val} ? ${concValues[i].val} is not increasing.`
                };
            }
        }

        return { ok: true };
    };

    const handleSubmit = async () => {
        const validation = validateRows(rows);
        if (!validation.ok) {
            setValidationMessage(validation.error);
            setOpenValidationDialog(true);
            return;
        }

        if (onCalibrate) onCalibrate();

        try {
            // Pass eMwMathModel from the UI selector into parameters override
            const augmentedParams = { ...parameters, eMwMathModel: Number(mathModel) };
            const apiData = await callApi();

            console.log("MICROWELL CALIBRATION RESPONSE:", apiData);

            const formatted = transformApiToHistory(apiData);
            if (!formatted) return;

            setResultDataMap(prev => {
                const prevData = prev || {};
                const nextRun = Object.keys(prevData).length + 1;
                const finalDescription =
                    description && description.trim() !== "" ? description : `Run ${nextRun}`;

                const newEntry = {
                    run: nextRun,
                    description: finalDescription,
                    result: formatted,
                    rows,
                    parameters: augmentedParams,
                    // Store the raw response so Predictions can use it as the
                    // assay definition for /microwell/predict calls.
                    rawResponse: apiData
                };

                const updated = { ...prevData, [nextRun]: newEntry };
                sessionStorage.setItem("microwellCalibrationHistory", JSON.stringify(updated));
                return updated;
            });

            if (onCalibrate) onCalibrate();
        } catch (err) {
            console.error("MICROWELL API ERROR:", err);
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "flex-start",
                backgroundColor: "#fff"
            }}
        >
            <Box
                sx={{
                    width: { xs: "100%", md: "80%" },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    p: 2,
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    alignItems: { xs: "stretch", sm: "center" }
                }}
            >
                <TextField
                    select
                    label="Math Model"
                    value={mathModel}
                    onChange={(e) => setMathModel(e.target.value)}
                    sx={{ flex: 1, minWidth: 160 }}
                    size="medium"
                >
                    {MATH_MODELS.map(m => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ flex: 3, minWidth: 200 }}
                    size="medium"
                />

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    startIcon={<ScienceIcon />}
                    disabled={loading}
                    sx={{
                        whiteSpace: "nowrap",
                        minWidth: 160,
                        minHeight: 56,
                        px: 3,
                        alignSelf: { xs: "stretch", sm: "auto" },
                        backgroundColor: "#774def",
                        "&:hover": { backgroundColor: "#5e3bc7" }
                    }}
                >
                    {loading ? "Calibrating..." : "Calibrate"}
                </Button>

                {error && (
                    <Typography color="error" sx={{ width: "100%", mt: { xs: 1, sm: 0 } }}>
                        {error}
                    </Typography>
                )}
            </Box>

            <ConfirmDialog
                open={openValidationDialog}
                title="Validation Error"
                message={validationMessage}
                onConfirm={() => setOpenValidationDialog(false)}
                onCancel={() => setOpenValidationDialog(false)}
            />
        </Box>
    );
}
